import React, { useEffect, useState } from 'react'
import { Gauge, Copy, ExternalLink, Settings2, Plus, XCircle } from 'lucide-react'
import api from '../api'
import { Card, PageHeader, Badge, Tabs, EmptyState, Field, Button, Alert, Modal, ModalHeader, Input } from '../components/ui'
import { isAdmin } from '../auth'

const numberInputStyle = {
  width: 110, padding: '8px 12px', borderRadius: 10,
  border: '1px solid var(--border-strong)', fontSize: 13.5, color: 'var(--text)',
  background: 'var(--surface)', outline: 'none', fontFamily: 'inherit'
}

const textareaStyle = {
  width: '100%', padding: '9px 12px', borderRadius: 10, minHeight: 80, resize: 'vertical',
  border: '1px solid var(--border-strong)', fontSize: 13.5, color: 'var(--text)',
  background: 'var(--surface)', outline: 'none', fontFamily: 'inherit'
}

const statusTone = { OPEN: 'warning', PENDING: 'neutral', PROSES: 'brand', SELESAI: 'success', DIABAIKAN: 'neutral' }
const statusLabel = { OPEN: 'OPEN', PENDING: 'PENDING', PROSES: 'PROSES', SELESAI: 'SELESAI', DIABAIKAN: 'DIABAIKAN' }

export default function TiketGangguan() {
  // Halaman ini SHARED (admin & teknisi, lihat Layout.jsx) -- teknisi cuma boleh lihat daftar +
  // salin link WA (cakupan yg dikonfirmasi user), config ambang & tombol Tambah/Batalkan tetap
  // admin-only krn backend-nya juga sudah digerbang requireAdmin (lihat routes/tiket.js).
  const admin = isAdmin()
  const [tiket, setTiket] = useState([])
  const [tab, setTab] = useState('OPEN')
  const [copiedId, setCopiedId] = useState(null)
  const [threshold, setThreshold] = useState('')
  const [thresholdSaved, setThresholdSaved] = useState(null)
  const [thresholdError, setThresholdError] = useState('')
  const [savingThreshold, setSavingThreshold] = useState(false)
  const [thresholdInfo, setThresholdInfo] = useState('')

  const [massThreshold, setMassThreshold] = useState('')
  const [massThresholdError, setMassThresholdError] = useState('')
  const [savingMassThreshold, setSavingMassThreshold] = useState(false)
  const [massThresholdInfo, setMassThresholdInfo] = useState('')

  const [showAdd, setShowAdd] = useState(false)
  const [newUsername, setNewUsername] = useState('')
  const [newKeluhan, setNewKeluhan] = useState('')
  const [addError, setAddError] = useState('')
  const [addSubmitting, setAddSubmitting] = useState(false)

  useEffect(() => { load() }, [tab])
  useEffect(() => { if (admin) loadThreshold() }, [])

  async function load() {
    const res = await api.get('/tiket', { params: { status: tab } })
    setTiket(res.data)
  }

  async function loadThreshold() {
    try {
      const res = await api.get('/tiket/pengaturan')
      setThreshold(String(res.data.redamanThreshold))
      setThresholdSaved(res.data.redamanThreshold)
      setMassThreshold(String(res.data.massAlertThreshold))
    } catch {}
  }

  async function saveThreshold() {
    setThresholdError(''); setThresholdInfo('')
    setSavingThreshold(true)
    try {
      const res = await api.post('/tiket/pengaturan', { redamanThreshold: threshold })
      setThresholdSaved(res.data.redamanThreshold)
      setThreshold(String(res.data.redamanThreshold))
      setThresholdInfo(
        res.data.diabaikan > 0
          ? `Ambang disimpan. ${res.data.diabaikan} tiket Terbuka otomatis ditandai "Diabaikan" krn redaman-nya sudah tidak memenuhi ambang baru.`
          : 'Ambang disimpan.'
      )
      if (tab === 'OPEN' || tab === 'DIABAIKAN') load()
    } catch (err) {
      setThresholdError(err.response?.data?.message || 'Gagal menyimpan')
    } finally { setSavingThreshold(false) }
  }

  async function saveMassThreshold() {
    setMassThresholdError(''); setMassThresholdInfo('')
    setSavingMassThreshold(true)
    try {
      const res = await api.post('/tiket/pengaturan', { massAlertThreshold: massThreshold })
      setMassThreshold(String(res.data.massAlertThreshold))
      setMassThresholdInfo('Ambang disimpan.')
    } catch (err) {
      setMassThresholdError(err.response?.data?.message || 'Gagal menyimpan')
    } finally { setSavingMassThreshold(false) }
  }

  function openAddModal() {
    setNewUsername(''); setNewKeluhan(''); setAddError('')
    setShowAdd(true)
  }

  async function submitAdd() {
    setAddError('')
    if (!newUsername.trim()) return setAddError('Username pelanggan wajib diisi')
    if (!newKeluhan.trim()) return setAddError('Keluhan wajib diisi')
    setAddSubmitting(true)
    try {
      await api.post('/tiket', { pppUsername: newUsername.trim(), keluhan: newKeluhan.trim() })
      setShowAdd(false)
      setTab('OPEN')
      load()
    } catch (err) {
      setAddError(err.response?.data?.message || 'Gagal membuat tiket')
    } finally { setAddSubmitting(false) }
  }

  function linkFor(token) {
    return `${window.location.origin}/tiket/${token}`
  }

  function copyLink(t) {
    navigator.clipboard.writeText(linkFor(t.token))
    setCopiedId(t.id)
    setTimeout(() => setCopiedId(null), 1500)
  }

  async function batalkanTiket(t) {
    const alasan = window.prompt(`Batalkan tiket "${t.ppp_username}"? Isi alasan (opsional):`)
    if (alasan === null) return
    try {
      await api.post(`/tiket/${t.id}/batalkan`, { alasan })
      load()
    } catch (err) {
      window.alert(err.response?.data?.message || 'Gagal membatalkan tiket')
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <PageHeader
          title="Tiket Gangguan"
          subtitle={`Otomatis dari monitoring redaman OLT (ambang ${thresholdSaved ?? '...'} dBm, scan tiap 15 menit) — atau dibuat manual oleh CS/Admin`}
        />
        {admin && <Button icon={Plus} onClick={openAddModal}>Tambah Tiket</Button>}
      </div>

      {showAdd && (
        <Modal onClose={() => setShowAdd(false)} width={440}>
          <ModalHeader title="Tambah Tiket Gangguan" subtitle="Buat tiket manual, mis. dari laporan telepon/WA langsung" onClose={() => setShowAdd(false)} />
          {addError && <Alert type="error">{addError}</Alert>}
          <Field label="Username Pelanggan (PPPoE)" style={{ marginBottom: 14 }}>
            <Input value={newUsername} onChange={e => setNewUsername(e.target.value)} placeholder="mis. budi-jaten" />
          </Field>
          <Field label="Keluhan" style={{ marginBottom: 18 }}>
            <textarea
              style={textareaStyle} value={newKeluhan} onChange={e => setNewKeluhan(e.target.value)}
              placeholder="Mis. internet mati total sejak pagi, WiFi tidak muncul, dll."
            />
          </Field>
          <Button loading={addSubmitting} onClick={submitAdd} style={{ width: '100%', padding: '12px' }}>
            {addSubmitting ? 'Menyimpan...' : 'Buat Tiket & Kirim Alert'}
          </Button>
        </Modal>
      )}

      {admin && (
      <Card style={{ padding: 18, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontWeight: 700, fontSize: 13.5 }}>
          <Settings2 size={16} color="var(--brand)" /> Ambang Redaman
        </div>
        {thresholdError && <Alert type="error">{thresholdError}</Alert>}
        {thresholdInfo && <Alert type="success">{thresholdInfo}</Alert>}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
          <Field label="Redaman lebih buruk dari (dBm)">
            <input
              type="number" step="0.5" style={numberInputStyle}
              value={threshold} onChange={e => setThreshold(e.target.value)}
            />
          </Field>
          <Button size="sm" loading={savingThreshold} onClick={saveThreshold}>Simpan</Button>
        </div>
        <p style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 8 }}>
          Tiket dibuat kalau redaman pelanggan LEBIH NEGATIF (lebih buruk) dari nilai ini. Contoh: isi <strong>-27</strong> supaya cuma redaman -27 dBm ke bawah (mis. -28, -30) yang dianggap gangguan, -25 s/d -27 dianggap masih wajar. Berlaku mulai siklus scan berikutnya, tidak perlu restart. Hanya berlaku utk tiket otomatis — tidak memengaruhi tiket manual.
        </p>

        <div style={{ borderTop: '1px solid var(--border)', margin: '16px 0' }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontWeight: 700, fontSize: 13.5 }}>
          <Settings2 size={16} color="var(--brand)" /> Ambang Gangguan Massal
        </div>
        {massThresholdError && <Alert type="error">{massThresholdError}</Alert>}
        {massThresholdInfo && <Alert type="success">{massThresholdInfo}</Alert>}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
          <Field label="Jumlah tiket sekaligus dlm 1 siklus scan">
            <input
              type="number" step="1" min="2" style={numberInputStyle}
              value={massThreshold} onChange={e => setMassThreshold(e.target.value)}
            />
          </Field>
          <Button size="sm" loading={savingMassThreshold} onClick={saveMassThreshold}>Simpan</Button>
        </div>
        <p style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 8 }}>
          Kalau dalam satu siklus scan (~15 menit) jumlah tiket yang terkonfirmasi Terbuka sekaligus mencapai angka ini atau lebih, sistem kirim <strong>satu WA ringkasan</strong> (dikelompokkan per OLT/port) — bukan satu WA per pelanggan. Menghindari grup kebanjiran notifikasi kalau ada gangguan massal (mis. kabel backbone/distribusi putus). Di bawah angka ini, tetap kirim WA detail per pelanggan seperti biasa.
        </p>
      </Card>
      )}

      <Tabs
        tabs={[['OPEN', 'Terbuka', Gauge], ['PROSES', 'Proses', Gauge], ['PENDING', 'Pending', Gauge], ['SELESAI', 'Selesai', Gauge], ['DIABAIKAN', 'Diabaikan', Gauge]]}
        active={tab} onChange={setTab}
      />

      {tab === 'PENDING' && (
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '-6px 0 12px' }}>
          Baru terdeteksi buruk sekali scan — belum dikonfirmasi, belum kirim WA. Kalau masih buruk di scan berikutnya (~15 menit lagi), otomatis naik jadi Terbuka. Kalau membaik, otomatis hilang dari daftar ini.
        </p>
      )}
      {tab === 'PROSES' && (
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '-6px 0 12px' }}>
          Teknisi sudah menindaklanjuti tapi belum selesai (mis. butuh alat/part tambahan). Lihat catatan kendala per baris, link masih aktif untuk teknisi lanjutkan ke "Selesai".
        </p>
      )}
      {tab === 'DIABAIKAN' && (
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '-6px 0 12px' }}>
          Tiket Terbuka yang otomatis ditutup krn ambang redaman dinaikkan (redaman-nya sudah dianggap wajar). Bukan diselesaikan teknisi — link WA lama tetap kasih pesan jelas kalau diklik, bukan error.
        </p>
      )}

      <Card style={{ overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#fafafc' }}>
              {['Dibuat', 'Pelanggan', 'IP', 'OLT', 'Redaman', 'Status', 'Aksi'].map(h => (
                <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: '1px solid var(--border)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tiket.length === 0 ? (
              <tr><td colSpan={7}><EmptyState title="Belum ada tiket" /></td></tr>
            ) : tiket.map(t => (
              <tr key={t.id} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ padding: '10px 16px', whiteSpace: 'nowrap' }}>{t.dibuat_at}</td>
                <td style={{ padding: '10px 16px', fontWeight: 600 }}>
                  {t.ppp_username}
                  {t.sumber === 'MANUAL' && <Badge tone="brand" style={{ marginLeft: 8, fontWeight: 600 }}>Manual</Badge>}
                  {(t.status === 'PROSES' || t.status === 'DIABAIKAN') && t.catatan_proses && (
                    <div style={{ fontWeight: 400, fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>{t.catatan_proses}</div>
                  )}
                  {t.sumber === 'MANUAL' && !(t.status === 'PROSES' || t.status === 'DIABAIKAN') && t.keluhan && (
                    <div style={{ fontWeight: 400, fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>{t.keluhan}</div>
                  )}
                </td>
                <td style={{ padding: '10px 16px' }}>{t.ip || '-'}</td>
                <td style={{ padding: '10px 16px' }}>{t.olt ? `${t.olt === 'hioso' ? 'Hioso' : t.olt === 'hsgq' ? 'HSGQ' : t.olt} (${t.port})` : '-'}</td>
                <td style={{ padding: '10px 16px', color: t.redaman != null ? 'var(--danger)' : 'var(--text-faint)', fontWeight: 600 }}>
                  {t.redaman != null ? `${t.redaman} dBm` : '-'}
                </td>
                <td style={{ padding: '10px 16px' }}>
                  <Badge tone={statusTone[t.status] || 'neutral'}>{statusLabel[t.status] || t.status}</Badge>
                </td>
                <td style={{ padding: '10px 16px' }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {(t.status === 'OPEN' || t.status === 'PROSES') && (
                      <>
                        <button onClick={() => copyLink(t)} title="Salin link" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                          <Copy size={15} />
                        </button>
                        <a href={linkFor(t.token)} target="_blank" rel="noreferrer" title="Buka" style={{ color: 'var(--text-muted)' }}>
                          <ExternalLink size={15} />
                        </a>
                      </>
                    )}
                    {admin && (t.status === 'OPEN' || t.status === 'PENDING' || t.status === 'PROSES') && (
                      <button onClick={() => batalkanTiket(t)} title="Batalkan tiket" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)' }}>
                        <XCircle size={15} />
                      </button>
                    )}
                    {copiedId === t.id && <span style={{ fontSize: 11, color: 'var(--success)' }}>Tersalin!</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  )
}
